import { useEffect, useMemo } from "react"
import { Controller, SubmitHandler, useForm, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  CountryIsoCode,
  EmploymentTemplate,
  Engagement,
  EngagementRemoteWorkCostAllowancePolicyInputType,
  EngagementRemoteWorkCostAllowancePolicyObjectType,
  Maybe,
  PaymentFrequencyEnum,
  ReimbursementPolicyEnum,
  VariableCompensationTypeEnum,
} from "@oysterhr/schema"
import { Alert, Button, FormLabel, Select, Text } from "@oysterhr/ui"
import { MoneyInput } from "@oysterhr/feature-components"
import noop from "lodash-es/noop"
import { VariableCompensationSummary } from "~/shared/components/VariableCompensationSummary"
import { convertSubunitIntoUnit, convertUnitIntoSubunit, allCurrencies } from "@oysterhr/common"
import { useEquipmentRemoteWorkCostAgnosticMutation } from "~/app/hooks/remote-work-costs/useEquipmentRemoteWorkCostAgnosticMutation"
import { RemoteWorkPolicyFormValues } from "./RemoteWorkPolicySection"
import * as Yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"
import type { MoneyInputValue } from "@oysterhr/feature-components"
import { RemoteWorkCostAllowancesItemDisplayPartial } from "./RemoteWorkCostAllowancesItemDisplayPartial"
import type { DocumentNode } from "graphql"
import { EntityType } from "../VariableCompensationSection/types"
import { FormErrorSummary } from "~/app/components/FormErrorSummary"
import { useVariableCompensationTranslations } from "~/app/hooks/variable-compensation/useVariableCompensationTranslations"

interface RemoteWorkCostAllowancesItemFormProps {
  allowanceRemoteWorkCost?: Maybe<EngagementRemoteWorkCostAllowancePolicyObjectType>
  entityId: Engagement["id"] | EmploymentTemplate["id"]
  entityType: EntityType
  salaryCurrencyCode: MoneyInputValue["currency"]
  countryCode?: Maybe<CountryIsoCode>
  showFormErrors?: boolean
  onCancel: () => void
  onSave: () => void
  refetchQueries?: Array<DocumentNode>
}

const policiesRequiringAmount = [
  ReimbursementPolicyEnum.MonthlyAllowance,
  ReimbursementPolicyEnum.OneTimeStipend,
]

export const RemoteWorkCostAllowancesItemForm = ({
  allowanceRemoteWorkCost,
  entityId,
  entityType,
  salaryCurrencyCode,
  countryCode,
  showFormErrors,
  onCancel,
  onSave,
  refetchQueries,
}: RemoteWorkCostAllowancesItemFormProps) => {
  const [t] = useTranslation("company_interface/CompanyNewHireFTEPage")
  const getGenericTranslation = useVariableCompensationTranslations({
    compensationType: "GENERIC",
    fileContext: "NewHireForm",
  })

  const defaultValues = useMemo(() => {
    const amountSubunit = allowanceRemoteWorkCost?.isStatutory
      ? allowanceRemoteWorkCost.topUpPolicySubunit
      : allowanceRemoteWorkCost?.allowancePolicySubunit
    return {
      allowancePolicy: allowanceRemoteWorkCost?.allowancePolicy || undefined,
      allowancePolicySubunit: allowanceRemoteWorkCost?.allowancePolicySubunit
        ? convertSubunitIntoUnit(amountSubunit, salaryCurrencyCode || "USD")
        : undefined,
    }
  }, [
    allowanceRemoteWorkCost?.allowancePolicy,
    allowanceRemoteWorkCost?.allowancePolicySubunit,
    allowanceRemoteWorkCost?.isStatutory,
    allowanceRemoteWorkCost?.topUpPolicySubunit,
    salaryCurrencyCode,
  ])

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        allowancePolicy: Yup.string().required(t("required.providedAllowancePolicy")),
        allowancePolicySubunit: Yup.mixed().when("allowancePolicy", {
          is: value =>
            !allowanceRemoteWorkCost?.isStatutory && policiesRequiringAmount.includes(value),
          then: Yup.number()
            .required(t("required.providedAllowancePolicyAmount"))
            .typeError(t("validation.number"))
            .positive(t("validation.positive")),
        }),
      }),
    [allowanceRemoteWorkCost?.isStatutory, t],
  )

  const { control, watch, handleSubmit, reset, trigger } = useForm<
    RemoteWorkPolicyFormValues["allowancePolicy"]
  >({
    defaultValues: defaultValues,
    resolver: yupResolver(validationSchema),
  })

  // Watchers
  const allowancePolicy = watch("allowancePolicy")
  const allowancePolicySubunit = watch("allowancePolicySubunit")

  // constants
  const allowancePolicyCanModifyAmount =
    !allowanceRemoteWorkCost?.isStatutory &&
    (allowancePolicy === ReimbursementPolicyEnum.MonthlyAllowance ||
      allowancePolicy === ReimbursementPolicyEnum.OneTimeStipend)

  const automaticWFHPolicy = allowanceRemoteWorkCost?.isStatutory

  // form options
  const allowancePolicyOptions = useMemo(() => {
    // The order of which the allowance policies should appear
    const allowancePolicyOrder = [
      ReimbursementPolicyEnum.OneTimeStipend,
      ReimbursementPolicyEnum.MonthlyAllowance,
      ReimbursementPolicyEnum.BasicReimbursement,
      ReimbursementPolicyEnum.NoReimbursement,
    ]

    return Object.values(allowanceRemoteWorkCost?.compatibleAllowancePolicies || [])
      .sort((a, b) => allowancePolicyOrder.indexOf(a) - allowancePolicyOrder.indexOf(b))
      .map(equipmentPolicy => ({
        label: t(`reimbursementPolicyEnum.${equipmentPolicy}`, { ns: "Common" }),
        value: equipmentPolicy,
      }))
  }, [allowanceRemoteWorkCost?.compatibleAllowancePolicies, t])

  // form helpers
  const [manageRemoteWorkCost, { loading: isCreating }] =
    useEquipmentRemoteWorkCostAgnosticMutation({
      entityType: entityType,
      options: {
        onCompleted: () => {
          onSave()
        },
        refetchQueries: refetchQueries || [],
        awaitRefetchQueries: true,
      },
    })

  const onCreateOrUpdate = (values: {
    allowancePolicy: EngagementRemoteWorkCostAllowancePolicyInputType
  }) => {
    manageRemoteWorkCost({
      variables: {
        input:
          entityType === "engagement"
            ? {
                employmentId: entityId,
                employmentAttributes: { remoteWorkCosts: values },
              }
            : {
                id: entityId,
                remoteWorkCosts: values,
              },
      },
    })
  }

  const handleOnSave: SubmitHandler<EngagementRemoteWorkCostAllowancePolicyInputType> = values => {
    const parameters = {
      allowancePolicy: {
        allowancePolicy: values.allowancePolicy,
        allowancePolicySubunit: policiesRequiringAmount.includes(values.allowancePolicy)
          ? convertUnitIntoSubunit(values.allowancePolicySubunit, salaryCurrencyCode || "USD")
          : null,
      },
    }
    onCreateOrUpdate(parameters)
    return values
  }

  // parent validations
  const { setValue: setParentValue, clearErrors: clearParentErrors } = useFormContext()

  useEffect(() => {
    if (setParentValue) {
      setParentValue("remoteWorkExpensePolicyEditingValidation", true)
    }
    return () => {
      clearParentErrors("remoteWorkExpensePolicyEditingValidation")
      setParentValue("remoteWorkExpensePolicyEditingValidation", false)
      reset()
    }
  }, [setParentValue, clearParentErrors, reset])

  useEffect(() => {
    if (showFormErrors) {
      trigger(undefined, { shouldFocus: true })
    }
  }, [trigger, showFormErrors])

  return (
    <div data-testid="allowancePolicy" className="mt-2 space-y-2 rounded-lg bg-gray-100 p-4">
      {allowanceRemoteWorkCost?.isStatutory && (
        <>
          <div className="divide-y">
            <Text className="mb-4 mt-1 font-semibold">
              {t(`reimbursementPolicyEnum.${allowanceRemoteWorkCost?.allowancePolicy}`, {
                ns: "Common",
              })}
            </Text>
            <RemoteWorkCostAllowancesItemDisplayPartial
              allowanceRemoteWorkCost={allowanceRemoteWorkCost}
              salaryCurrencyCode={salaryCurrencyCode}
              countryCode={countryCode}
            />
          </div>

          <Controller
            name="allowancePolicySubunit"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <MoneyInput
                currencies={allCurrencies.filter(c => c.currencyCode === salaryCurrencyCode)}
                /* This is because we want this currency code to always be the same as the salary */
                value={{
                  currency: salaryCurrencyCode,
                  amount: value || "",
                }}
                onChange={({ amount }) => onChange(amount)}
                error={error?.message}
                label={
                  <FormLabel
                    label={t("remoteWorkCosts.allowancePolicy.labels.addAboveStatutoryFormLabel")}
                  />
                }
              />
            )}
          />
        </>
      )}
      {!allowanceRemoteWorkCost?.isStatutory && (
        <Controller
          name="allowancePolicy"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Select
              label={
                <FormLabel
                  label={
                    allowancePolicy
                      ? t(`reimbursementPolicyEnum.${allowancePolicy}`, { ns: "Common" })
                      : t("label.reimbursementPolicy")
                  }
                />
              }
              placeholder={t("placeholder.reimbursementPolicy")}
              id={field.name}
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              error={error?.message}
              disabled={automaticWFHPolicy}
            >
              {Object.values(allowancePolicyOptions).map(allowancePolicy => (
                <Select.Option value={allowancePolicy.value} key={allowancePolicy.value}>
                  {allowancePolicy.label}
                </Select.Option>
              ))}
            </Select>
          )}
        />
      )}
      {allowancePolicyCanModifyAmount && (
        <>
          <Select
            disabled={true}
            onChange={noop}
            placeholder={t("restrictiveCovenants.paymentType.fields.amountType.options.fixed")}
          />
          <div className="grid grid-cols-2 gap-x-2">
            <Controller
              name="allowancePolicySubunit"
              control={control}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <MoneyInput
                  currencies={allCurrencies.filter(c => c.currencyCode === salaryCurrencyCode)}
                  /* This is because we want this currency code to always be the same as the salary */
                  value={{
                    currency: salaryCurrencyCode,
                    amount: value || "",
                  }}
                  onChange={({ amount }) => onChange(amount)}
                  error={error?.message}
                />
              )}
            />
            <Select
              disabled={true}
              onChange={noop}
              placeholder={
                allowancePolicy === ReimbursementPolicyEnum.MonthlyAllowance
                  ? t("frequencyOptions.MONTHLY", {
                      ns: "reimbursement_budget/Common",
                    })
                  : t("frequencyOptions.NEVER", {
                      ns: "reimbursement_budget/Common",
                    })
              }
            />
          </div>
          <div>
            <hr className="my-3" />
          </div>
          <VariableCompensationSummary
            compensationType={VariableCompensationTypeEnum.Allowance}
            type="amount"
            amount={{
              subunit: convertUnitIntoSubunit(
                parseFloat(allowancePolicySubunit || 0),
                salaryCurrencyCode || "USD",
              ),
              currencyCode: salaryCurrencyCode || "USD",
            }}
            frequency={
              allowancePolicy === ReimbursementPolicyEnum.MonthlyAllowance
                ? PaymentFrequencyEnum.Monthly
                : PaymentFrequencyEnum.Yearly
            }
            displayProratedSummary={false}
            prorated={false}
          />
        </>
      )}
      {/* We only want to show the alert only if the user selected the "BASIC_REIMBURSEMENT" policy  */}
      {allowancePolicy === ReimbursementPolicyEnum.BasicReimbursement && (
        <>
          <hr />
          <div>
            <Alert variation="info" className="mt-3">
              <Alert.Icon />
              <Alert.Content>
                {t("inlineAlerts.reimbursementPolicies.BASIC_REIMBURSEMENT")}
              </Alert.Content>
            </Alert>
          </div>
        </>
      )}
      <div>
        <div className="mt-6 flex space-x-2">
          {allowanceRemoteWorkCost?.active && (
            <Button type="button" variation="borderless" onClick={onCancel}>
              {t("cancel")}
            </Button>
          )}
          <Button type="button" isLoading={isCreating} onClick={handleSubmit(handleOnSave)}>
            {t("save")}
          </Button>
        </div>
      </div>
      <FormErrorSummary
        className="mt-4"
        isVisible={showFormErrors}
        title={getGenericTranslation("errors.editing.title")}
        description={getGenericTranslation("errors.editing.description")}
      />
    </div>
  )
}
